import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Mail, Phone, MapPin, Instagram, Youtube, Facebook, Twitter, Linkedin } from 'lucide-react';
import SEO from '../components/SEO';
import Breadcrumbs from '../components/Breadcrumbs';
import NotFoundPage from './NotFoundPage';
import { useSite } from '../contexts/SiteContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function SitePage() {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const { settings } = useSite();

  useEffect(() => {
    setLoading(true); setNotFound(false);
    axios.get(`${API}/site/pages/${slug}`)
      .then(r => setPage(r.data.page))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="max-w-3xl mx-auto px-4 py-16 text-center text-slate-400">Yükleniyor...</div>;
  if (notFound || !page) return <NotFoundPage title="Sayfa bulunamadı" message="Aradığınız içerik taşınmış ya da yayından kaldırılmış olabilir." />;

  // Build description from first 160 chars of content
  const metaDesc = (page.content || '').replace(/\s+/g, ' ').trim().slice(0, 157) + ((page.content || '').length > 157 ? '…' : '');

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <SEO title={page.title} description={metaDesc} canonicalPath={`/sayfa/${page.slug}`} />
      <Breadcrumbs items={[{ name: page.title }]} />
      <h1 className="text-3xl sm:text-4xl font-extrabold text-slate-800 mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>{page.title}</h1>
      <div className="prose max-w-none text-slate-700 whitespace-pre-wrap leading-relaxed" data-testid="site-page-content">
        {page.content}
      </div>

      {slug === 'iletisim' && (
        <div className="mt-10 border-t border-slate-100 pt-8 grid sm:grid-cols-2 gap-6">
          {settings.contact_email && <ContactRow icon={Mail} label="E-posta" value={settings.contact_email} href={`mailto:${settings.contact_email}`} />}
          {settings.contact_phone && <ContactRow icon={Phone} label="Telefon" value={settings.contact_phone} href={`tel:${settings.contact_phone.replace(/\s+/g, '')}`} />}
          {settings.contact_address && <ContactRow icon={MapPin} label="Adres" value={settings.contact_address} />}
          <div className="sm:col-span-2 flex flex-wrap gap-3 pt-2">
            {settings.social_instagram && <SocialBtn icon={Instagram} url={settings.social_instagram} />}
            {settings.social_youtube && <SocialBtn icon={Youtube} url={settings.social_youtube} />}
            {settings.social_twitter && <SocialBtn icon={Twitter} url={settings.social_twitter} />}
            {settings.social_facebook && <SocialBtn icon={Facebook} url={settings.social_facebook} />}
            {settings.social_linkedin && <SocialBtn icon={Linkedin} url={settings.social_linkedin} />}
          </div>
        </div>
      )}
    </div>
  );
}

function ContactRow({ icon: Icon, label, value, href }) {
  const Row = (
    <div className="flex items-start gap-3">
      <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center shrink-0"><Icon className="w-5 h-5 text-emerald-600" /></div>
      <div className="min-w-0">
        <div className="text-xs text-slate-500">{label}</div>
        <div className="font-medium text-slate-800 break-all">{value}</div>
      </div>
    </div>
  );
  return href ? <a href={href} className="hover:bg-slate-50 rounded-xl p-2 -m-2 transition-colors" data-testid={`contact-${label.toLowerCase()}`}>{Row}</a> : <div data-testid={`contact-${label.toLowerCase()}`}>{Row}</div>;
}

function SocialBtn({ icon: Icon, url }) {
  return (
    <a href={url} target="_blank" rel="noreferrer noopener" className="w-10 h-10 rounded-xl border border-slate-200 hover:border-emerald-500 hover:text-emerald-600 flex items-center justify-center text-slate-500 transition-colors" data-testid={`social-link-${url}`}>
      <Icon className="w-5 h-5" />
    </a>
  );
}
